import React, { useEffect, useState } from "react";
import { updateBankDetails } from "../../../apis/customers";
import { uploadFile } from "../../../apis/auth";
import EmpSuccess from "../../hrm/employees/employeeSuccess";
import { all_routes } from "../../router/all_routes";
import { errorAlert, successAlert } from "../../../style/scss/utils/Alerts";

interface GuarantorInfo {
  name: string;
  mobile: string;
  email: string;
  address: string;
  aadhar_number: string;
  photo: string;
  aadharFront: string;
  aadharBack: string;
  panCardImage: string;
}

interface FormData {
  guarantor: {
    first: GuarantorInfo;
    second: GuarantorInfo;
  };
}

interface BankDetailsProps {
  customerData: any;
}

const GuarantorDetails: React.FC<BankDetailsProps> = ({ customerData }) => {
  const user = customerData?.allData;
  const user_type = JSON.parse(sessionStorage.getItem("info")).type;

  const [formData, setFormData] = useState<FormData>({
    guarantor: {
      first: {
        name: "",
        mobile: "",
        email: "",
        aadhar_number: "",
        address: "",
        photo: "",
        aadharFront: "",
        aadharBack: "",
        panCardImage: "",
      },
      second: {
        name: "",
        mobile: "",
        email: "",
        address: "",
        aadhar_number: "",
        photo: "",
        aadharFront: "",
        aadharBack: "",
        panCardImage: "",
      },
    },
  });
  const [successCreation, setSuccessCreation] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  useEffect(() => {
    if (user?.guarantor) {
      let parsedGuarantor = user.guarantor;

      // If it's a string, parse it
      if (typeof parsedGuarantor === "string") {
        try {
          parsedGuarantor = JSON.parse(user.guarantor);
        } catch (e) {
          console.error("Failed to parse guarantor JSON:", e);
          return;
        }
      }

      setFormData((prev) => ({
        guarantor: {
          first: {
            ...prev.guarantor.first,
            ...parsedGuarantor.first,
          },
          second: {
            ...prev.guarantor.second,
            ...parsedGuarantor.second,
          },
        },
      }));
    }
  }, [user]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    guarantorType: "first" | "second",
    field: keyof GuarantorInfo
  ) => {
    setFormData((prev) => ({
      ...prev,
      guarantor: {
        ...prev.guarantor,
        [guarantorType]: {
          ...prev.guarantor[guarantorType],
          [field]: e.target.value,
        },
      },
    }));
  };

  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    guarantorType: "first" | "second",
    field: keyof GuarantorInfo
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append("image", file);

      const response = await uploadFile(formData);
      const fileUrl = response.data.image;

      setFormData((prev) => ({
        ...prev,
        guarantor: {
          ...prev.guarantor,
          [guarantorType]: {
            ...prev.guarantor[guarantorType],
            [field]: fileUrl,
          },
        },
      }));
    } catch (error) {
      console.error("File upload failed:", error);
      alert("File upload failed!");
    }
  };

  const handleSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!user?.id || isSubmitting) return;

    // Take one immutable snapshot so first and second guarantor are always
    // sent together, even when React state changes while the request is active.
    const guarantor = {
      first: { ...formData.guarantor.first },
      second: { ...formData.guarantor.second },
    };

    setIsSubmitting(true);
    try {
      const res = await updateBankDetails({
        userId: user.id,
        guarantor,
      });
      if (res.data.status === true) {
        const modalElement = document.getElementById("show_guarantor");
        const closeButton = modalElement?.querySelector<HTMLButtonElement>(
          '[data-bs-dismiss="modal"]'
        );

        // Wait for Bootstrap to remove its backdrop before opening SweetAlert.
        closeButton?.click();
        await new Promise((resolve) => window.setTimeout(resolve, 250));
        await successAlert(
          "Success",
          "Guarantor and co-applicant details saved successfully."
        );
      } else {
        errorAlert(
          "Error",
          res.data.message || "Guarantor details could not be saved."
        );
      }
    } catch (error) {
      console.error("Guarantor update failed:", error);
      errorAlert("Error", "Guarantor details could not be saved. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isGuarantorDataBlank = () => {
    if (!user?.guarantor) return true;

    let parsed;
    try {
      parsed =
        typeof user.guarantor === "string"
          ? JSON.parse(user.guarantor)
          : user.guarantor;
    } catch (e) {
      return true; // Treat as blank on parse error
    }

    const isEmpty = (obj: any) =>
      !obj || Object.values(obj).every((val) => !val || val === "");

    return isEmpty(parsed.first) && isEmpty(parsed.second);
  };
  const [showGurrantor, setShowGurrantor] = useState(false);

  return (
    <div className="modal fade" id="show_guarantor">
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h3>Guarantor Details</h3>
            <button
              type="button"
              className="btn-close custom-btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            >
              <i className="ti ti-x" />
            </button>
          </div>
          <div className="modal-body theme_new_color">
            {user && (
              <>
                <h4>First Guarantor</h4>
                <div className="row">
                  {Object.keys(formData.guarantor.first).map((key) => (
                    <div className="col-md-6 mb-3" key={key}>
                      <label className="form-label">
                        {key === "aadharFront"
                          ? "Aadhar Front"
                          : key === "aadharBack"
                            ? "Aadhar Back"
                            : key === "panCardImage"
                              ? "PAN Card Image"
                            : key === "photo"
                              ? "Photo"
                              : key.charAt(0).toUpperCase() + key.slice(1)}
                      </label>
                      <input
                        type={
                          ["photo", "aadharFront", "aadharBack", "panCardImage"].includes(key)
                            ? "file"
                            : "text"
                        }
                        className="form-control"
                        accept={
                          ["photo", "aadharFront", "aadharBack", "panCardImage"].includes(key)
                            ? "image/*"
                            : undefined
                        }
                        value={
                          ["photo", "aadharFront", "aadharBack", "panCardImage"].includes(key)
                            ? undefined
                            : (formData.guarantor.first as any)[key]
                        }
                        onChange={(e) =>
                          ["photo", "aadharFront", "aadharBack", "panCardImage"].includes(key)
                            ? handleFileChange(
                              e,
                              "first",
                              key as keyof GuarantorInfo
                            )
                            : handleChange(
                              e,
                              "first",
                              key as keyof GuarantorInfo
                            )
                        }
                      />
                      {["photo", "aadharFront", "aadharBack", "panCardImage"].includes(key) &&
                        (formData.guarantor.first as any)[key] && (
                          <div className="mt-2">
                            <img
                              src={(formData.guarantor.first as any)[key]}
                              alt="Preview"
                              style={{
                                maxWidth: "100%",
                                maxHeight: "150px",
                                border: "1px solid #ddd",
                                borderRadius: "5px",
                              }}
                            />
                          </div>
                        )}
                    </div>
                  ))}
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <h4>Co-Applicant</h4>
                  <h4
                    style={{ cursor: "pointer" }}
                    onClick={() => setShowGurrantor(!showGurrantor)}
                  >
                    {showGurrantor ? "Hide" : "Show"}
                  </h4>
                </div>
                {showGurrantor && (
                  <div className="row">
                    {Object.keys(formData.guarantor.second).map((key) => (
                      <div className="col-md-6 mb-3" key={key}>
                        <label className="form-label">
                          {key === "aadharFront"
                            ? "Aadhar Front"
                            : key === "aadharBack"
                              ? "Aadhar Back"
                              : key === "panCardImage"
                                ? "PAN Card Image"
                              : key === "photo"
                                ? "Photo"
                                : key.charAt(0).toUpperCase() + key.slice(1)}
                        </label>
                        <input
                          type={
                            ["photo", "aadharFront", "aadharBack", "panCardImage"].includes(key)
                              ? "file"
                              : "text"
                          }
                          className="form-control"
                          accept={
                            ["photo", "aadharFront", "aadharBack", "panCardImage"].includes(key)
                              ? "image/*"
                              : undefined
                          }
                          value={
                            ["photo", "aadharFront", "aadharBack", "panCardImage"].includes(key)
                              ? undefined
                              : (formData.guarantor.second as any)[key]
                          }
                          onChange={(e) =>
                            ["photo", "aadharFront", "aadharBack", "panCardImage"].includes(key)
                              ? handleFileChange(
                                e,
                                "second",
                                key as keyof GuarantorInfo
                              )
                              : handleChange(
                                e,
                                "second",
                                key as keyof GuarantorInfo
                              )
                          }
                        />
                        {["photo", "aadharFront", "aadharBack", "panCardImage"].includes(key) &&
                          (formData.guarantor.second as any)[key] && (
                            <div className="mt-2">
                              <img
                                src={(formData.guarantor.second as any)[key]}
                                alt="Preview"
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "150px",
                                  border: "1px solid #ddd",
                                  borderRadius: "5px",
                                }}
                              />
                            </div>
                          )}
                      </div>
                    ))}
                  </div>
                )}
                {/* {(user?.type === "financer" ||
                  (["retailer", "employee"].includes(user?.type) &&
                    isFormDataBlank())) && (
                  <div className="text-end mt-3">
                    <button className="btn btn-primary" onClick={handleSubmit}>
                      Submit
                    </button>
                  </div>
                )} */}
                {(isGuarantorDataBlank() || user_type === "financer") && (
                  <div className="text-end mt-3">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                    >
                      {isSubmitting
                        ? "Saving..."
                        : isGuarantorDataBlank()
                          ? "Submit"
                          : "Update"}
                    </button>
                  </div>
                )}
                {/* {(user?.type === "financer" || !isFormDataBlank()) && (
                  <div className="text-end mt-3">
                    <button className="btn btn-primary" onClick={handleSubmit}>
                      Submit
                    </button>
                  </div>
                )} */}
              </>
            )}
          </div>
        </div>
      </div>
      {successCreation && <EmpSuccess all_routes={all_routes} />}
    </div>
  );
};

export default GuarantorDetails;
